import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
/**
 * Possible statuses of the active scroll activity
 */
var DragScrollStatus = /*#__PURE__*/function (DragScrollStatus) {
  DragScrollStatus[DragScrollStatus["IDLE"] = 0] = "IDLE";
  // There is no active scroll activity
  DragScrollStatus[DragScrollStatus["STARTED"] = 1] = "STARTED";
  // A scroll is possibly in progress
  DragScrollStatus[DragScrollStatus["STOPPED"] = 2] = "STOPPED"; // A scroll has completed, but has not been evaluated yet
  return DragScrollStatus;
}(DragScrollStatus || {});
/**
 *    This class is used to help facilitate scrolling to the bottom of a data grid, specifically a variable-rowheight grid
 *
 *    With a variable-rowheight data grid, the user can drag the vertical scrollbar handle all the way to the bottom.  If all
 *    the rows have not yet been measured, the grid code will measure some or all of the remaining rows based on the scrollTop
 *    position.  If the newly-measured rows wind up being larger that the default row height, the grid then adjusts its
 *    scrolling container accordingly, expanding it to fit the newly-measured rows.  What it does NOT do, however, is attempt
 *    to figure out what rows should have been in the viewport, and then subsequently scroll to keep those rows in the viewport.
 *
 *    This class attempts to realize the user's intention of scrolling to the very bottom of the grid.  It does this in the following way:
 *    - When we detect a potential scroll start (via a mousedown on the grid's scroll container), we keep track of the scrollHeight
 *    of the grid at that point.
 *    - When the scroll is completed, we check the new scroll position of the scroll container.  (We have to check both the position when
 *    we receive a mouseup event, as well as possibly the next scroll event after the moouseup)
 *    - If the scroll position is such that it is at the bottom of what was the original scrollHeight (with a very small threshold -
 *    for some reason we never get exactly to the bottom), we consider that to be a scroll to the bottom of the grid, and
 *    force a scroll to the bottom
 *
 *    There are a couple of checks that also happen to attempt to mitigate unwanted behavior:
 *    We cap the time the user scrolls.  This way, if they are dragging the scrollbar around for a while, we do nothing
 *    We also make sure that the mouse position at the start and end of the drag indicate a downward scroll
 *
 */
export class DataGridScrollToBottomHelper {
  constructor(grid, scrollToBottomFn) {
    /** The amount of time we listen from the beginning of the scroll to the end.  Anything past this we ignore the scroll */
    _defineProperty(this, "SCROLL_TIMEOUT", 1000);
    /** The fudge-factor we use to determine if the user scrolled to the bottom.  This is a percentage of the original scrollHeight */
    _defineProperty(this, "BOTTOM_THRESHOLD", 0.005);
    /** A reference to the grid being watched */
    _defineProperty(this, "grid", void 0);
    /** A callback function to call when we determine we want to force the scroll to the bottom of the grid */
    _defineProperty(this, "scrollToBottomFn", void 0);
    /** The current status of the active scroll activity */
    _defineProperty(this, "dragScrollStatus", DragScrollStatus.IDLE);
    /** The Y position of the pointer when the scroll started (mousedown) */
    _defineProperty(this, "scrollDragStartY", -1);
    /** The Y position of the pointer when the scroll ended (mouseup) */
    _defineProperty(this, "scrollDragStopY", -1);
    /** The scrollHeight of the grid when the scroll started */
    _defineProperty(this, "startScrollHeight", -1);
    /** The clientHeight of the grid when the scroll started */
    _defineProperty(this, "clientHeight", -1);
    /** The scrollTop of the grid when the scroll ended */
    _defineProperty(this, "scrollStopTop", -1);
    /** The timer used to limit the amount of time we listen for the end of the scroll */
    _defineProperty(this, "scrollTimer", null);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.grid = grid;
    this.scrollToBottomFn = scrollToBottomFn;
    this.overrideGridFunctionality();
  }

  /**
   * Handle the mousedown event on the grid's scrolling container
   */
  onMouseDown(evt) {
    if (this.dragScrollStatus !== DragScrollStatus.IDLE) {
      this.reset();
    }
    if (evt.target !== this.getScrollingContainer()) {
      // The user clicked on something inside the container, not the container itself - they did not click on the scrollbar
      return;
    }

    // Record the relevant information for the start of the scroll activity
    const {
      scrollHeight,
      clientHeight
    } = this.getScrollingContainer();
    this.scrollDragStartY = evt.clientY;
    this.startScrollHeight = scrollHeight;
    this.clientHeight = clientHeight;
    this.dragScrollStatus = DragScrollStatus.STARTED;

    // Start a timer, after which we will ignore the scroll activity
    this.scrollTimer = setTimeout(() => {
      this.reset();
    }, this.SCROLL_TIMEOUT);
  }

  /**
   * Notification from the grid that it has scrolled
   */
  gridScrolled(scrollTop) {
    // If we haven't received the mouseup yet, we will just ignore this scroll
    if (this.dragScrollStatus === DragScrollStatus.STOPPED) {
      this.scrollStopTop = scrollTop;
      this.determineIfShouldScrollToBottom();
      this.reset();
    }
  }

  /**
   * Handle the mouseup event on the grid's scrolling container
   */
  onMouseUp(evt) {
    if (this.dragScrollStatus !== DragScrollStatus.STARTED) {
      this.reset();
    }
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }

    // Record the relevant information from the end of the scroll activity
    this.scrollDragStopY = evt.clientY;
    this.scrollStopTop = this.getScrollingContainer().scrollTop;

    /*
    Due to the way the react-virtualized grid works, we wind up checking two scenarios - the position at the time of
    the mouseup, and the first scroll event that occurs after the mouseup.  Either of these could contain the information
    we need to determine if the user is attempting to scroll to the bottom of the grid
     */
    if (this.determineIfShouldScrollToBottom()) {
      // If we got the information we need from the mouseup event, we don't need to listen for the next scroll event
      this.reset();
    } else {
      // This indicates that the scroll activity has stopped, and that we should check out the next scroll event
      this.dragScrollStatus = DragScrollStatus.STOPPED;
    }
  }

  /**
   *  Based on the information we received from both the start and end of the scroll activity, determine if the user's
   *  intention was to scroll to the bottom of the grid
   *
   *  @return True if we have forced the scroll to the bottom of the grid
   */
  determineIfShouldScrollToBottom() {
    // Did the user drag the cursor down?
    if (this.scrollDragStopY > 0 || this.scrollDragStartY > 0 || this.scrollDragStopY > this.scrollDragStartY) {
      // Did we collect all of the information we need to make a decision?
      if (this.startScrollHeight > 0 && this.clientHeight > 0 && this.scrollStopTop > 0) {
        // Determine what the maximum scrollTop was at the time when the scroll activity started
        const maxScrollTop = this.startScrollHeight - this.clientHeight;

        // Determine how far off we are from the max
        const diff = maxScrollTop - this.scrollStopTop;
        const diffPct = diff / maxScrollTop;
        if (diffPct < this.BOTTOM_THRESHOLD) {
          // Need the timeout to let the grid finish up first
          setTimeout(() => this.scrollToBottomFn(), 0);
          return true;
        }
      }
    }
    return false;
  }

  /**
   *  Reset this helper to its initial state
   */
  reset() {
    this.dragScrollStatus = DragScrollStatus.IDLE;
    this.scrollDragStartY = -1;
    this.scrollDragStopY = -1;
    this.scrollStopTop = -1;
    this.startScrollHeight = -1;
    this.clientHeight = -1;
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
  }

  /**
   *  Cleanup the event listeners when the grid is removed
   */
  destroy() {
    const scrollingContainer = this.getScrollingContainer();
    if (scrollingContainer) {
      scrollingContainer.removeEventListener("mousedown", this.onMouseDown);
      scrollingContainer.removeEventListener("mouseup", this.onMouseUp);
    }
  }

  /**
   *  Reaches into the grid's private properties to get a handle to the actual DOM element that has the scrollbar
   */
  getScrollingContainer() {
    // @ts-ignore - we are reaching into the grid to access a private variable
    return this.grid?._scrollingContainer;
  }

  /**
   * Add our own event listeners to the grid's scrolling container
   */
  overrideGridFunctionality() {
    const scrollingContainer = this.getScrollingContainer();
    if (scrollingContainer) {
      scrollingContainer.addEventListener("mousedown", this.onMouseDown);
      scrollingContainer.addEventListener("mouseup", this.onMouseUp);
    }
  }
}
//# sourceMappingURL=DataGridScrollToBottomHelper.js.map
